import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../core/api/api';
import * as commonActions from './action-types';
import authHandler from '../core/api/auth-handler';
import * as appServiceLines from '../core/service-lines';
import { authenticationChannel } from '../core/authentication-channel/authentication-channel';
import * as commonEnums from '../enums/index';
import * as commonActionUtils from './_action-utilities';
import * as cmxDateTime from '../core/date-time/cmx-date-time';

import * as cmxErrors from '../core/errors';

// actions
import { fetchEula } from './eula.actions';
import { caseLookupError, caseSearchAvailable, fetchCases } from './launch-case-lookup';
import {
  fetchProcessInstance,
  getCasesWithEncounter,
  getDocumentsAsHtml,
  getFlagItems,
  getCCICustomClaimCrossCases,
  fetchOrderTypeStatus,
  getCodesWithEncounter,
  fetchRelatedCodes,
  fetchProviderRoleTypeConfigurations,
  fetchRelateCaseFilterDictionaries
} from './code-bench.actions';
import { loadCase, loadCaseFromURL } from './cases.action';

// util functions
import { loadXCaliber } from './xcaliber.actions';
import { fetchImprovementOptions } from './improvements.actions';
import { isIPC, isPathology, isSurgery, buildCaseDetailUrl } from '../core/service-lines';
import { isResourceAllowed } from '../core/amplify/utils/bench-editable';
import { shimContext } from './_action-utilities';
import { caseEditsActions } from './case-edits.actions';
import { navigationIntent } from './navigation-intent.action';

export { navigationIntent } from './navigation-intent.action';
export type {
  NavigationIntent,
  NavigationIntentPayload
} from './navigation-intent.action';

import { relatedCasesSortablePageable } from '../core/amplify/utils/pagination-utils';
import { getRelatedCasesInitialFilters } from '../core/amplify/utils/common-filter-utils';

const { AmplifyAuthenticate, Launch, EpicLaunchPoints } = commonActions;
const { UnauthenticatedError, PageNotFoundError } = cmxErrors;

const REQUEST_INIT_OPTIONS: RequestInit = {
  method: 'GET',
  cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
  redirect: 'follow',
  referrer: 'no-referrer'
};

// --------------------- sync actions  --------------------- //

const userDetailsAvailable = createAction<CMxAPI.ContextAPI>(
  AmplifyAuthenticate.USER_DETAILS
);
const launchParamsAvailable = createAction<CMxAPI.LaunchParams>(
  Launch.PARAMS_AVAILABLE
);
const contextParams = createAction<any>(Launch.CONTEXT_PARAMS);
const hostCaseId = createAction<{ hostCaseId: number }>(Launch.PARENT_CASE);

const billingGroupAvailable = createAction<CMxAPI.PageableList<CMxAPI.Case>>(
  Launch.BILLING_GROUP
);
const caseRunAvailable = createAction<CMxAPI.CaseRun>(Launch.CASE_DETAIL);

const caseRunWithRefreshAvailable = createAction<CMxAPI.CaseRun>(
  Launch.CASE_DETAIL_WITH_REFRESH
);

const epicToken = createAction<string>(Launch.EPIC_TOKEN);

const selectCase = createAsyncThunk<
  CMxAPI.CaseRun,
  CMxAPI.CaseRun,
  { rejectValue: CMxCommonApp.CMxError }
>(Launch.CASE_SELECTED, async (caseRun, { dispatch }) => {
  const { path: slPath, caseDetailVersion } = appServiceLines.get();
  const url = `${slPath}/caseinstance/${caseRun.case_id}/details/${caseDetailVersion}?evidence=true&processTypes=PREDICTION&processTypes=ADDENDUM`;
  const withDetails = await api.requestJSON<CMxAPI.Case>(url);
  //
  const activeItem = withDetails.caseRuns[0];
  await loadCase(activeItem, dispatch);
  return activeItem;
});

type PopupLaunchParmas = {
  token: string;
  caseId: string;
  serviceLine: string;
};

const fetchContextJSON = async () => {
  const userDetailsResponse = await api.request('/contexts/v1');
  const contextJSON: CMxAPI.ContextAPI = await userDetailsResponse.json();
  return contextJSON;
};

type LoadFromUrlState = {
  login?: {
    auth?: {
      token?: string | null;
    };
  };
  ui?: {
    context?: CMx.Context;
  };
};

const getLoadedContext = (state: LoadFromUrlState): CMx.Context | null => {
  const context = state?.ui?.context;
  return context?.activeContext ? context : null;
};

const syncAuthTokenFromState = (state: LoadFromUrlState) => {
  if (authHandler.token) {
    return;
  }

  const token = state?.login?.auth?.token ?? state?.ui?.context?.jwtToken;
  if (token) {
    authHandler.token = token;
  }
};

const tokenAvailable = createAsyncThunk<
  void,
  PopupLaunchParmas,
  { rejectValue: CMxCommonApp.CMxError }
>(
  Launch.LOAD_CASE_WITH_TOKEN,
  async (launchParams: PopupLaunchParmas, { dispatch }) => {
    authHandler.token = launchParams.token;
    const sl = appServiceLines.determineServiceLineConfig({
      name: launchParams.serviceLine,
      caseIdentifier: `${launchParams.caseId}`
    });
    appServiceLines.set(sl);

    const contextJSON: CMxAPI.ContextAPI = await fetchContextJSON();

    dispatch(userDetailsAvailable(contextJSON));

    const { path: slPath, caseDetailVersion } = appServiceLines.get();
    const url = `${slPath}/caseinstance/${launchParams.caseId}/details/${caseDetailVersion}?evidence=true&processTypes=PREDICTION&processTypes=ADDENDUM`;
    const withDetails = await api.requestJSON<CMxAPI.Case>(url);
    //
    const activeItem = withDetails.caseRuns[0];
    const caseUrl = (await loadCase(activeItem, dispatch)).url;

    dispatch(caseRunAvailable(activeItem));
    dispatch(navigationIntent({ intent: 'CASE_DETAIL', url: caseUrl }));
    return;
  }
);

const loadCaseFromId = createAsyncThunk<
  void,
  Pick<PopupLaunchParmas, 'caseId'>,
  { rejectValue: CMxCommonApp.CMxError }
>(Launch.LOAD_CASE_FROM_ID, async (launchParams, { dispatch }) => {
  const { path: slPath, caseDetailVersion } = appServiceLines.get();

  const url = `${slPath}/caseinstance/${launchParams.caseId}/details/${caseDetailVersion}?evidence=true&processTypes=PREDICTION&processTypes=ADDENDUM`;
  const withDetails = await api.requestJSON<CMxAPI.Case>(url);
  //
  const activeItem = withDetails.caseRuns[0];
  await loadCase(activeItem, dispatch);
  const serviceLineConfig = appServiceLines.determineServiceLineConfig({
    name: activeItem.service_line,
    caseIdentifier: `${activeItem.case_uid || activeItem.case_id}`
  });

  const caseId =
    serviceLineConfig.caseIdProp in activeItem
      ? activeItem[serviceLineConfig.caseIdProp]
      : activeItem.case_id;

  const sl = activeItem.service_line?.toLowerCase() ?? '';
  dispatch(caseRunAvailable(activeItem));
  
  const pathname = window.location.pathname;
  const matchWorklist = pathname.match(/^\/cmx\/(worklist|coding-quality)/);
  const basePath = matchWorklist ? matchWorklist[0] : '/cmx/worklist';
  
  dispatch(navigationIntent({ 
    intent: 'CASE_DETAIL', 
    url: `${basePath}/${sl}/case/${caseId}/case-run/${activeItem.process_instance_id}`,
    replace: true
  }));
  return;
});

const embeddedLaunch = createAsyncThunk<
  CMxAPI.Case | CMxAPI.Case[],
  URLSearchParams,
  { rejectValue: CMxCommonApp.CMxError }
>(AmplifyAuthenticate.AUTHENTICATE, async (authRequest, { dispatch }) => {
  if (!authRequest.has('data') || !authRequest.has('appId')) {
    dispatch(navigationIntent({ intent: 'NOT_AUTHORIZED' }));
    throw new Error(`missing request data`);
  }

  // ------------ launch point ------------
  const appId = encodeURIComponent(authRequest.get('appId'));
  const data = encodeURIComponent(authRequest.get('data'));
  const searchParams = `appId=${appId}&data=${data}`;
  let launchRequest;

  // ------------ authentication ------------
  try {
    launchRequest = await api.request(
      '/externalApplication/authenticate/v1' + `?` + searchParams,
      { ...REQUEST_INIT_OPTIONS }
    );
  } catch (e) {
    dispatch(navigationIntent({ intent: 'NOT_AUTHORIZED' }));
    throw new UnauthenticatedError(`unauthenticated`);
  }

  // ------------ launch parameters ------------
  const launchParams = await launchRequest.json();
  dispatch(launchParamsAvailable(launchParams));

  const userDetailsResponse = await api.request('/contexts/v1');
  const contextJSON: CMxAPI.ContextAPI = await userDetailsResponse.json();

  // ------------ eula check ------------
  const location = userDetailsResponse.headers.get('location');
  const eulaLocation = '/eula/';
  if (location === eulaLocation) {
    dispatch(fetchEula(contextJSON));
    dispatch(navigationIntent({ intent: 'EULA' }));
  }
  dispatch(userDetailsAvailable(contextJSON));

  // ------------ launch point ------------
  // The launchPoint may come through as a quoted string.
  // We strip the string of any quotation marks
  const launchPoint = `${launchParams.launchPoint}`.replace(/[\"\']/g, '');

  if (launchPoint === EpicLaunchPoints.ERROR) {
    dispatch(navigationIntent({ intent: 'CONTEXT_UNAVAILABLE' }));
    throw new PageNotFoundError('Unable to determine Context');
  } else if (launchPoint === EpicLaunchPoints.ERROR2) {
    dispatch(navigationIntent({ intent: 'SESSION_OPENED' }));
    throw new PageNotFoundError('Session already opened');
  }

  // ------------ case lookup ------------
  if (location !== eulaLocation) {
    const caseDetails = await fetchCases(launchParams, dispatch);
    // ------------ xcaliber token fetch ------------
    await loadXCaliber(dispatch);
    return caseDetails;
  } else {
    return null;
  }
});

type SimpleParams = {
  params: {
    [key: string]: string;
  };
};

/**
 * Method to load case details with refresh as true
 * This grabs latest data from XC
 * @param url
 * @param processInstanceIdProp
 * @param match
 * @param dispatch
 * @returns
 */
const loadCaseRunWithRefresh = async (
  url: string,
  processInstanceIdProp: string,
  match: SimpleParams,
  dispatch: any
) => {
  if (!url) return;
  const urlWithRefresh = `${url}&refresh=true`;
  const withRefreshDetails = await api.requestJSON<CMxAPI.Case>(
    urlWithRefresh,
    {},
    true
  );
  let activeItemWithRefresh = withRefreshDetails.caseRuns.find(
    caseRun =>
      caseRun[processInstanceIdProp].toString() === match.params.id.toString()
  );
  if (!activeItemWithRefresh) {
    activeItemWithRefresh = withRefreshDetails.caseRuns[0];
  }
  dispatch(caseRunWithRefreshAvailable(activeItemWithRefresh));
};

const loadFromURLMatch = createAsyncThunk<
  CMxAPI.Case | CMxAPI.Case[],
  SimpleParams,
  { rejectValue: CMxCommonApp.CMxError; state: LoadFromUrlState }
>(Launch.LOAD_FROM_URL, async (match, { dispatch, getState }) => {
  const state = getState() as any;
  syncAuthTokenFromState(state);

  const loadedContext = getLoadedContext(state);
  const contextJSON: CMxAPI.ContextAPI = loadedContext ?? (await fetchContextJSON());
  const shimmedContext = loadedContext ?? shimContext(contextJSON);
  dispatch(userDetailsAvailable(contextJSON));

  // Check feature flag for display_all_case_runs
  const useV3Endpoint = shimmedContext.activeContext
    ? isResourceAllowed(
        shimmedContext.activeContext,
        commonEnums.ResourceId.DISPLAY_ALL_CASE_RUNS,
        match.params.serviceLine
      )
    : false;

  const sl = appServiceLines.determineServiceLineConfig({
    name: match.params.serviceLine,
    displayAllCaseRuns: useV3Endpoint
  });

  const processInstanceIdProp = appServiceLines.determineProcessInstanceId({
    name: match.params.serviceLine
  });

  // Use utility function to build URL
  const url = buildCaseDetailUrl(
    match.params.caseId,
    sl
  );

  const withDetails = await api.requestJSON<CMxAPI.Case>(url, {}, true);

  // Use v3 logic for pathology
  let activeItem = withDetails.caseRuns.find(
    caseRun =>
      caseRun[processInstanceIdProp].toString() === match.params.id.toString()
  );

  if (!activeItem) {
    activeItem = withDetails.caseRuns[0];
  }

  if (isIPC(activeItem.service_line)) {
    await dispatch(getDocumentsAsHtml(activeItem));
  }

  await loadCaseFromURL(activeItem, dispatch);

  if (!!withDetails && match.params?.getCaserunWithRefresh) {
    try {
      loadCaseRunWithRefresh(url, processInstanceIdProp, match, dispatch);
    } catch (error) {
      console.error(
        'Error while trying to load case run with refresh ',
        error?.message
      );
    }
  }

  let processInstance;
  try {
    processInstance = await dispatch(fetchProcessInstance(activeItem)).unwrap();
  } catch (e) {
    // ignore
  }

  // mock for tests
  if (!processInstance) {
    // do nothing
  }

  if (isIPC(activeItem.service_line) || isSurgery(activeItem.service_line)) {
    dispatch(
      caseEditsActions.loadAllPatientDetailOptions({
        activeTenantId: activeItem.tenant_uid,
        facility: activeItem.facility
      })
    );
  }
  // ------------ xcaliber token fetch ------------
  await loadXCaliber(dispatch);

  try {
    if (isIPC(activeItem?.service_line)) {
      await dispatch(getFlagItems(activeItem as CMxAPI.CaseRunV2));
    }
  } catch (error) {
    console.error(`Error while fetching flags: ${error.message}`);
  }
  const serviceLineConfig = appServiceLines.determineServiceLineConfig({
    name: activeItem.service_line,
    caseIdentifier: `${activeItem.case_uid || activeItem.case_id}`
  });

  try {
    await dispatch(
      fetchImprovementOptions({
        activeItem: activeItem,
        processInstance: processInstance
      })
    );
  } catch (error) {
    console.error(`Error while fetching improvement options: ${error.message}`);
  }

  try {
    await dispatch(
      fetchProviderRoleTypeConfigurations({
        activeItem: activeItem,
        processInstance: processInstance
      })
    );
  } catch (error) {
    console.error(
      `Error while fetching provider role type configuration: ${error.message}`
    );
  }

  try {
    if (!isPathology(activeItem?.service_line)) {
      dispatch(
        getCCICustomClaimCrossCases({
          activeItem: activeItem,
          processInstance: processInstance
        })
      );
    }

    dispatch(
      fetchOrderTypeStatus({
        activeItem: activeItem,
        processInstance: processInstance
      })
    );

    let filters;
    try {
      filters = await dispatch(
        fetchRelateCaseFilterDictionaries({
          activeItem: activeItem,
          processInstance: processInstance
        })
      ).unwrap();
    } catch (e) {
      // ignore
    }
    let startDate: Date;
    let endDate: Date;
    if (activeItem.admission_datetime && activeItem.discharge_datetime) {
      startDate = activeItem.admission_datetime
      endDate = activeItem.discharge_datetime
    } else {
      startDate = cmxDateTime.format(
        new Date(),
        cmxDateTime.FORMATS.DATE_TIME_WITH_ZONE,
        {
          throw: false,
          localized: true,
          offset: {
            action: cmxDateTime.DateManipulateOperation.SUBTRACT,
            delta: [30, cmxDateTime.DateManipulateUnit.DAY]
          }
        }
      )
      endDate = new Date()
    }


    const filterSelection = getRelatedCasesInitialFilters(
      filters,
      startDate,
      endDate
    );

    if (activeItem?.encounter_identifier?.number) {
      // Currently Pathology does not support this method of related cases, therefore skip if Path
      // Added for AUTO-6674
      if (!isPathology(activeItem.service_line)) {
        dispatch(
          getCasesWithEncounter({
            encounterId: activeItem?.encounter_identifier?.number,
            sortablePageable: { ...relatedCasesSortablePageable },
            serviceLine: serviceLineConfig.amplifyPath,
            filterSelection
          })
        );
      }
      dispatch(
        fetchRelatedCodes({
          encounterId: activeItem?.encounter_identifier?.number,
          serviceLine: serviceLineConfig.amplifyPath,
          codeType: 'ICD',
          caseId: activeItem.case_id
        })
      );
      dispatch(
        fetchRelatedCodes({
          encounterId: activeItem?.encounter_identifier?.number,
          serviceLine: serviceLineConfig.amplifyPath,
          codeType: 'CPT',
          caseId: activeItem.case_id
        })
      );
      dispatch(
        fetchRelatedCodes({
          encounterId: activeItem?.encounter_identifier?.number,
          serviceLine: serviceLineConfig.amplifyPath,
          codeType: 'MODIFIER',
          caseId: activeItem.case_id
        })
      );
    }
  } catch (error) {
    console.error(`Error while fetching related cases: ${error.message}`);
  }
  return withDetails;
});

const standaloneLaunch = createAsyncThunk<
  CMxAPI.Case | CMxAPI.Case[],
  URLSearchParams,
  { rejectValue: CMxCommonApp.CMxError }
>(AmplifyAuthenticate.AUTOMATE, async (searchParams, { dispatch }) => {
  if (searchParams.has('channel')) {
    try {
      await authenticationChannel.ready;

      const params = {};
      // Context params can be passed in via a queryString
      [...searchParams.keys()].forEach((key: string) => {
        let value: any;

        // special case for processInstanceGuids which are an
        // array.
        if (key === 'processInstanceGuids') {
          value = searchParams.get(key).split(',');
        } else {
          value = searchParams.get(key);
        }
        params[key] = value;
      });
      dispatch(contextParams(params));

      const sl = appServiceLines.determineServiceLineConfig({
        name: searchParams.get('serviceLine'),
        caseIdentifier: `${searchParams.get('caseId')}`
      });
      appServiceLines.set(sl);

      const url = `${sl.path}/caseinstance/${searchParams.get(
        'caseId'
      )}/details/${
        sl.caseDetailVersion
      }?evidence=true&processTypes=PREDICTION&processTypes=ADDENDUM`;

      const withDetails = await api.requestJSON<CMxAPI.Case>(url);
      //
      const processInstanceGuid = searchParams.get('processInstanceGuid');
      let activeItem = withDetails.caseRuns.find(
        caseRun => caseRun.process_instance_guid === processInstanceGuid
      );
      if (!activeItem) {
        activeItem = withDetails.caseRuns[0];
      }

      const contextJSON: CMxAPI.ContextAPI = await fetchContextJSON();
      dispatch(userDetailsAvailable(contextJSON));

      const useGuid = searchParams.get('useGuid') ?? 'false';
      const caseLoadResult = await loadCase(activeItem, dispatch, useGuid);
      dispatch(navigationIntent({ intent: 'CASE_DETAIL', url: caseLoadResult.url }));
      await loadXCaliber(dispatch);

      return withDetails;
    } catch (e) {
      dispatch(navigationIntent({ intent: 'NOT_AUTHORIZED' }));
    }
  }
});

export {
  epicToken,
  loadFromURLMatch,
  standaloneLaunch,
  hostCaseId,
  loadCaseFromId,
  tokenAvailable,
  contextParams,
  launchParamsAvailable,
  selectCase,
  embeddedLaunch,
  userDetailsAvailable,
  caseRunAvailable,
  caseRunWithRefreshAvailable,
  caseSearchAvailable,
  caseLookupError,
  billingGroupAvailable,
  fetchCases
};
